import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { financialOpsService } from '../../services/financial-ops.service';
import { apiErrorMessage } from '../../utils/apiError';
import {
  productsService,
  type BillingAccountingInput,
  type BillingTaxConfigInput,
} from '../../services/products.service';

type Company = { id: string; name: string; active: boolean };
type Version = BillingAccountingInput & {
  id: string;
  company: Company;
  taxConfigs: BillingTaxConfigInput[];
};

type Props = { productId: number; productName: string };

const today = new Date();
const initialTax = (): BillingTaxConfigInput => ({
  year: today.getFullYear(),
  month: today.getMonth() + 1,
  rbt12: 0,
  annex: 'III',
  requiresFactorR: false,
  factorR: null,
});
const emptyForm = (): BillingAccountingInput => ({
  companyId: '',
  effectiveYear: today.getFullYear(),
  effectiveMonth: today.getMonth() + 1,
  metadataKey: 'app',
  metadataValue: '',
  taxConfigs: [initialTax()],
});

function competence(version: Pick<Version, 'effectiveYear' | 'effectiveMonth'>) {
  return `${version.effectiveYear}-${String(version.effectiveMonth).padStart(2, '0')}`;
}

export function BillingAccountingTab({ productId, productName }: Props) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BillingAccountingInput>(emptyForm);
  const {
    data: billingData,
    isLoading: billingLoading,
    error: billingError,
  } = useQuery({
    queryKey: ['product-billing-accounting', productId],
    queryFn: () => productsService.getBillingAccounting(productId),
    // Produto que não é o DocuGen responde 400 com a explicação; repetir não
    // muda nada e só enche o log.
    retry: false,
  });
  const { data: companiesData } = useQuery({
    queryKey: ['financial-ops', 'companies', 'active-for-billing'],
    queryFn: () => financialOpsService.listCompanies({ active: true, limit: 100 }),
  });
  const versions: Version[] = billingData?.data?.versions ?? billingData?.versions ?? [];
  const companies: Company[] = companiesData?.data?.data ?? companiesData?.data ?? [];
  const selectedVersion = useMemo(
    () =>
      versions.find(
        (version) =>
          competence(version) ===
          `${form.effectiveYear}-${String(form.effectiveMonth).padStart(2, '0')}`
      ),
    [form.effectiveMonth, form.effectiveYear, versions]
  );

  useEffect(() => {
    if (selectedVersion) {
      setForm({
        companyId: selectedVersion.companyId,
        effectiveYear: selectedVersion.effectiveYear,
        effectiveMonth: selectedVersion.effectiveMonth,
        metadataKey: selectedVersion.metadataKey,
        metadataValue: selectedVersion.metadataValue,
        taxConfigs:
          selectedVersion.taxConfigs.length > 0 ? selectedVersion.taxConfigs : [initialTax()],
      });
    }
  }, [selectedVersion]);

  useEffect(() => {
    if (!form.companyId && companies.length > 0) {
      const solvyxa = companies.find((company) => company.name.toLowerCase().includes('solvyxa'));
      setForm((current) => ({ ...current, companyId: (solvyxa ?? companies[0]).id }));
    }
  }, [companies, form.companyId]);

  const saveMutation = useMutation({
    mutationFn: () => productsService.saveBillingAccounting(productId, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-billing-accounting', productId] });
      toast.success('Configuração contábil salva');
    },
    // O backend explica o motivo (produto errado, empresa inválida, RBT12
    // zerado); engolir isso num texto fixo transforma um erro autoexplicativo
    // em investigação.
    onError: (err) =>
      toast.error(apiErrorMessage(err, 'Não foi possível salvar a configuração contábil')),
  });

  const setTax = (patch: Partial<BillingTaxConfigInput>) => {
    setForm((current) => ({ ...current, taxConfigs: [{ ...current.taxConfigs[0], ...patch }] }));
  };
  const setEffectiveCompetence = (
    patch: Pick<BillingAccountingInput, 'effectiveYear' | 'effectiveMonth'>
  ) => {
    setForm((current) => ({
      ...current,
      ...patch,
      taxConfigs: current.taxConfigs.map((taxConfig) => ({ ...taxConfig, ...patch })),
    }));
  };

  if (billingLoading) {
    return <p className="text-ink-tertiary">Carregando configuração contábil...</p>;
  }
  // Sem isto o formulário aparecia normal e só reclamava no submit — o produto
  // errado (nome igual, slug diferente) não dava nenhum sinal antes disso.
  if (billingError) {
    return (
      <div className="rounded-md border border-danger/40 bg-danger/5 p-4">
        <p className="font-medium text-ink-primary">
          Billing contábil indisponível para {productName}
        </p>
        <p className="mt-1 text-sm text-ink-secondary">
          {apiErrorMessage(billingError, 'Não foi possível carregar a configuração contábil.')}
        </p>
      </div>
    );
  }
  const tax = form.taxConfigs[0] ?? initialTax();

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-ink-primary">Billing contábil — {productName}</h3>
        <p className="mt-1 text-sm text-ink-secondary">
          A configuração é versionada por competência. Alterações nesta tela afetam somente a
          competência selecionada; versões anteriores permanecem preservadas para os relatórios
          históricos.
        </p>
      </div>

      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-1">
        <p>Sem RBT12, Anexo e Fator R quando aplicável, o DAS estimado não pode ser calculado.</p>
        <p className="font-medium">As taxas da Stripe não reduzem a base de cálculo do DAS.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="label">Empresa</label>
          <select
            className="input w-full"
            value={form.companyId}
            onChange={(event) => setForm({ ...form, companyId: event.target.value })}
            required
          >
            <option value="">Selecione a empresa</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Mês</label>
            <input
              className="input w-full"
              type="number"
              min={1}
              max={12}
              value={form.effectiveMonth}
              onChange={(event) =>
                setEffectiveCompetence({
                  effectiveMonth: Number(event.target.value),
                  effectiveYear: form.effectiveYear,
                })
              }
            />
          </div>
          <div>
            <label className="label">Ano</label>
            <input
              className="input w-full"
              type="number"
              min={2020}
              max={2100}
              value={form.effectiveYear}
              onChange={(event) =>
                setEffectiveCompetence({
                  effectiveYear: Number(event.target.value),
                  effectiveMonth: form.effectiveMonth,
                })
              }
            />
          </div>
        </div>
        <div>
          <label className="label">Chave de metadata</label>
          <input
            className="input w-full"
            value={form.metadataKey}
            onChange={(event) => setForm({ ...form, metadataKey: event.target.value })}
            required
          />
        </div>
        <div>
          <label className="label">Valor de metadata</label>
          <input
            className="input w-full"
            value={form.metadataValue}
            onChange={(event) => setForm({ ...form, metadataValue: event.target.value })}
            placeholder="docugen"
            required
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left text-ink-secondary">
            <tr>
              <th className="p-3">Competência</th>
              <th className="p-3">RBT12</th>
              <th className="p-3">Anexo</th>
              <th className="p-3">Sujeito ao Fator R</th>
              <th className="p-3">Fator R</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-3 whitespace-nowrap">
                {String(form.effectiveMonth).padStart(2, '0')}/{form.effectiveYear}
              </td>
              <td className="p-3">
                <input
                  className="input w-36"
                  type="number"
                  min={0}
                  step="0.01"
                  value={tax.rbt12 || ''}
                  onChange={(event) => setTax({ rbt12: Number(event.target.value) })}
                />
              </td>
              <td className="p-3">
                <select
                  className="input"
                  value={tax.annex}
                  onChange={(event) => setTax({ annex: event.target.value as 'III' | 'V' })}
                >
                  <option value="III">Anexo III</option>
                  <option value="V">Anexo V</option>
                </select>
              </td>
              <td className="p-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={tax.requiresFactorR}
                    onChange={(event) =>
                      setTax({
                        requiresFactorR: event.target.checked,
                        factorR: event.target.checked ? tax.factorR : null,
                      })
                    }
                  />{' '}
                  Sim
                </label>
              </td>
              <td className="p-3">
                <input
                  className="input w-28"
                  type="number"
                  min={0}
                  step="0.0001"
                  disabled={!tax.requiresFactorR}
                  value={tax.factorR ?? ''}
                  onChange={(event) =>
                    setTax({
                      factorR: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                  placeholder="0,28"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        className="btn-primary flex items-center gap-2"
        onClick={() => saveMutation.mutate()}
        disabled={
          !form.companyId ||
          !form.metadataKey.trim() ||
          !form.metadataValue.trim() ||
          saveMutation.isPending
        }
      >
        {saveMutation.isPending ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4" />}{' '}
        Salvar competência
      </button>

      {versions.length > 0 && (
        <div className="text-sm text-ink-tertiary">
          Competências já versionadas: {versions.map((version) => competence(version)).join(', ')}.
        </div>
      )}
    </div>
  );
}
